// ConvoCX app shell — side navigation config (CON-853).
// Nav sections + items, role-gated per the RBAC permission model (CON-833):
// agents see inbox + contacts + reports + help; workspace admins and super
// admins additionally see teams, channels, templates, settings and billing.
// Icons are inline stroke SVGs (24px, currentColor) matching the mockup.

import type { ReactNode } from 'react';

export type AgentRole = string | null | undefined;

export interface ShellNavItem {
  name: string;
  label: string;
  href: string;
  icon: ReactNode;
  /** Item requires workspace-admin (or above) role. */
  adminOnly?: boolean;
}

export interface ShellNavSection {
  title: string;
  items: ShellNavItem[];
}

const svg = (children: ReactNode): ReactNode => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const NAV_ICONS = {
  inbox: svg(<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />),
  contacts: svg([<path key="a" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />, <circle key="b" cx="9" cy="7" r="4" />, <path key="c" d="M23 21v-2a4 4 0 0 0-3-3.87" />, <path key="d" d="M16 3.13a4 4 0 0 1 0 7.75" />]),
  teams: svg([<path key="a" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />, <circle key="b" cx="9" cy="7" r="4" />, <path key="c" d="M23 21v-2a4 4 0 0 0-3-3.87" />, <path key="d" d="M16 3.13a4 4 0 0 1 0 7.75" />]),
  reports: svg([<line key="a" x1="12" y1="20" x2="12" y2="10" />, <line key="b" x1="18" y1="20" x2="18" y2="4" />, <line key="c" x1="6" y1="20" x2="6" y2="16" />]),
  templates: svg([<rect key="a" x="3" y="3" width="7" height="7" rx="1" />, <rect key="b" x="14" y="3" width="7" height="7" rx="1" />, <rect key="c" x="14" y="14" width="7" height="7" rx="1" />, <rect key="d" x="3" y="14" width="7" height="7" rx="1" />]),
  channels: svg([<circle key="a" cx="12" cy="12" r="10" />, <line key="b" x1="2" y1="12" x2="22" y2="12" />, <path key="c" d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />]),
  labels: svg([<path key="a" d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />, <line key="b" x1="7" y1="7" x2="7.01" y2="7" />]),
  knowledge: svg([<path key="a" d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />, <path key="b" d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />]),
  csat: svg(<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />),
  canned: svg([<path key="a" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />, <line key="b" x1="8" y1="9" x2="16" y2="9" />, <line key="c" x1="8" y1="13" x2="13" y2="13" />]),
  notifications: svg([<path key="a" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />, <path key="b" d="M13.73 21a2 2 0 0 1-3.46 0" />]),
  help: svg([<path key="a" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />, <circle key="b" cx="12" cy="10" r="3" />]),
  settings: svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>),
  billing: svg([<rect key="a" x="2" y="5" width="20" height="14" rx="2" />, <line key="b" x1="2" y1="10" x2="22" y2="10" />]),
} as const;

const ADMIN_ROLES = ['workspace_admin', 'super_admin_s1', 'super_admin_s2'];

export function isAdminRole(role: AgentRole): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

export const NAV_SECTIONS: ShellNavSection[] = [
  {
    title: 'Workspace',
    items: [
      { name: 'inbox', label: 'Inbox', href: '#/inbox', icon: NAV_ICONS.inbox },
      { name: 'contacts', label: 'Contacts & CRM', href: '#/contacts', icon: NAV_ICONS.contacts },
      { name: 'reports', label: 'Reports', href: '#/reports', icon: NAV_ICONS.reports },
      { name: 'notifications', label: 'Notifications', href: '#/notifications', icon: NAV_ICONS.notifications },
    ],
  },
  {
    title: 'Manage',
    items: [
      { name: 'teams', label: 'Teams', href: '#/teams', icon: NAV_ICONS.teams, adminOnly: true },
      { name: 'channels', label: 'Channels', href: '#/channels', icon: NAV_ICONS.channels, adminOnly: true },
      { name: 'templates', label: 'Message Templates', href: '#/templates', icon: NAV_ICONS.templates, adminOnly: true },
      { name: 'canned', label: 'Canned Responses', href: '#/canned', icon: NAV_ICONS.canned, adminOnly: true },
      { name: 'labels', label: 'Tags & Labels', href: '#/labels', icon: NAV_ICONS.labels, adminOnly: true },
      { name: 'knowledge', label: 'Knowledge Base', href: '#/knowledge', icon: NAV_ICONS.knowledge, adminOnly: true },
      { name: 'csat', label: 'CSAT', href: '#/csat', icon: NAV_ICONS.csat, adminOnly: true },
    ],
  },
  {
    title: 'Support',
    items: [
      { name: 'help', label: 'Help Centre', href: '#/help', icon: NAV_ICONS.help },
      { name: 'settings', label: 'Settings', href: '#/settings', icon: NAV_ICONS.settings, adminOnly: true },
      { name: 'billing', label: 'Billing', href: '#/billing', icon: NAV_ICONS.billing, adminOnly: true },
    ],
  },
];

/** Nav sections filtered by role (least-privilege). */
export function visibleNavSections(role: AgentRole): ShellNavSection[] {
  const admin = isAdminRole(role);
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.adminOnly || admin),
  })).filter((section) => section.items.length > 0);
}